import { matchPath } from 'react-router-dom';
import {
  globalHiddenRroutesDef,
  globalNavRoutesDef,
  companyHiddenRoutesDef,
  companyNavRoutesDef,
  companyCommandMenuRoutesDef,
} from '@/modules/app/routes';
import { NavGroup } from '@/hooks/useNavGroups';
import { getRouteSpecificity } from '@/hooks/useRoutes';
import {
  registerManagerModuleResolver,
  AnalyticsModule,
  ModuleResolution,
  toRoutePattern,
} from './taxonomy';

/**
 * Manager-only half of the product analytics module taxonomy. Deliberately
 * split out of taxonomy.ts: this module imports `@/modules/app/routes`,
 * which declares ~350 `React.lazy()` manager screens, and taxonomy.ts is
 * reached by chokepoints shared with maquinista (SentryRoute.tsx,
 * analyticsLink.ts). Importing routes.tsx from there measurably pulled the
 * whole manager route tree into the maquinista build (+3.4MB / +352 chunks
 * — see taxonomy.ts's header comment). This file must only ever be
 * imported from packages/frontend/src/manager.jsx.
 */

type FlatManagerRoute = {
  path: string;
  exact?: boolean;
  name: string;
  analyticsModule: AnalyticsModule;
};

// The five NavGroup[] tables that make up the manager route manifest. This
// taxonomy is DERIVED from them (route name, pattern) and every route
// declares its `analyticsModule` explicitly — see the contract doc.
const MANAGER_ROUTE_GROUPS: NavGroup[][] = [
  globalHiddenRroutesDef,
  globalNavRoutesDef,
  companyHiddenRoutesDef,
  companyNavRoutesDef,
  companyCommandMenuRoutesDef,
];

// Mirrors the flattening in packages/frontend/src/hooks/useRoutes.tsx
// (navItems, plus one level of nested items) — NOT its permission
// filtering, which is irrelevant here. An item with its own `path` is a route
// even when it also has `items` (Checklists → Plantillas), like useRoutes'
// `ownRoute`; an item without a path (a dropdown-only parent) only
// contributes its sub-items.
const flattenManagerRoutes = (groups: NavGroup[][]): FlatManagerRoute[] => {
  const entries: FlatManagerRoute[] = [];
  for (const group of groups.flat()) {
    for (const item of group.navItems) {
      if (item.path) {
        entries.push({
          path: item.path,
          exact: item.exact,
          name: item.name,
          analyticsModule: item.analyticsModule,
        });
      }
      for (const subItem of item.items ?? []) {
        if (!subItem.path) continue;
        entries.push({
          path: subItem.path,
          exact: subItem.exact,
          name: subItem.name,
          analyticsModule: subItem.analyticsModule,
        });
      }
    }
  }
  return entries;
};

/**
 * Manager routes are declared as `/:company?/:businessUnit?/:project?/...`;
 * the leading run of optional params is the context prefix, not the
 * screen, so the route pattern reported to PostHog drops it:
 * `/:company?/:businessUnit?/:project?/machine/:id` → `/machine/:id`, and
 * the context-only root → `/`.
 */
const stripContextPrefix = (path: string): string => {
  const stripped = path.replace(/^(\/:[A-Za-z0-9_]+\?)+/, '');
  return stripped === '' ? '/' : toRoutePattern(stripped);
};

let cachedManagerRoutes: FlatManagerRoute[] | undefined;
const getFlattenedManagerRoutes = (): FlatManagerRoute[] => {
  if (!cachedManagerRoutes) {
    cachedManagerRoutes = flattenManagerRoutes(MANAGER_ROUTE_GROUPS);
  }
  return cachedManagerRoutes;
};

export const resolveManagerModuleForPath = (
  pathname: string
): ModuleResolution => {
  const entries = getFlattenedManagerRoutes();
  let best: FlatManagerRoute | undefined;
  let bestSpecificity = -Infinity;
  for (const entry of entries) {
    const match = matchPath(pathname, {
      path: entry.path,
      exact: entry.exact,
    });
    if (!match) continue;
    const specificity = getRouteSpecificity(entry.path);
    if (specificity > bestSpecificity) {
      best = entry;
      bestSpecificity = specificity;
    }
  }

  if (!best) return { module: 'unknown' };

  return {
    module: best.analyticsModule,
    routeName: best.name,
    routePattern: stripContextPrefix(best.path),
  };
};

/** Wires manager-route resolution into taxonomy.ts's shared registry. Call
 * once, from manager.jsx only — never from maquinista.jsx. */
export const registerManagerTaxonomy = (): void => {
  registerManagerModuleResolver(resolveManagerModuleForPath);
};
